import { RecurringSchedule } from 'mbs-ui-shared@models/ScheduleModel';
import { DefaultSelectData } from 'mbs-ui-shared@models/SelectData';
import { ScheduleStepValue, syntheticFullDefaults } from 'mbs-ui-shared@models/backup/schedule-models';
import moment from 'moment';
import { DayOfWeek } from 'wizards/models/DayOfWeek.enum';
import { WeekNumber } from 'wizards/models/WeekNumber.enum';
import { AdvancedOptionsFlags, AdvancedOptionsStepValue, BlockSizeEnum } from 'wizards/models/advanced-options-models';
import { ArchiveConsistencyStepValue, defaultFailureMinutes } from 'wizards/models/archive-consistency-step-value';
import { CompressionEncryptionStepValue } from 'wizards/models/compression-encription-models';
import { RetentionPolicyStepValue, getDefaultGFSPolicySettings } from 'wizards/models/retention-policy-models';
import { ScheduleAdvancedStepValue, ScheduleFormSettings } from 'wizards/models/schedule-advanced-models';
import { SimpleScheduleStepValue } from 'wizards/models/simple-schedule-models';
import { BackupTargetVolumesEnum, WhatBackupStepValue } from 'wizards/models/what-backup-models';
import { BaseFormModels } from '../base/base-form-models';

export class IBBFormModel extends BaseFormModels {
  whatBackupStep: WhatBackupStepValue = { BackupVolumes: BackupTargetVolumesEnum.SystemRequired, partitions: [], valid: false };
  compressionEncryptionStep: CompressionEncryptionStepValue = {
    UseCompression: true,
    UseEncryption: false,
    EncryptionAlgorithm: 3,
    EncryptionKeySize: 256,
    EncryptionPassword: '',
    UseServerSideEncryption: false,
    valid: true
  };
  retentionPolicyStep: RetentionPolicyStepValue;
  archiveConsistencyStep: ArchiveConsistencyStepValue;
  advancedOptionsStep: AdvancedOptionsStepValue = {
    UseS3Acceleration: false,
    StorageClass: 1,
    azureAccessTier: 0,
    ExcludeEnabled: false,
    excludeRules: [],
    SyntheticFull: true,
    IgnoreBadSectors: true,
    DisableVSS: false,
    useSystemVSSProvider: false,
    PrefetchBlockCount: 100,
    BlockSize: BlockSizeEnum['1 MB'],
    valid: true
  };
  advancedOptionsFlags: AdvancedOptionsFlags = {
    ExcludePossible: true,
    UseS3AccelerationPossible: false,
    AzureAccessTierType: false,
    StorageClassSettingPossible: false,
    SyntheticFullSettingPossible: false,
    IgnoreBadSectorsPossible: true,
    DisableVSSPossible: true,
    UseVSSPossible: true,
    PrefetchBlockCountPossible: true,
    BlockSizeSettingPossible: true
  };
  scheduleStep: ScheduleStepValue;
  simpleScheduleStep?: SimpleScheduleStepValue;
  scheduleAdvancedStep?: ScheduleAdvancedStepValue;
  AvailableMachinePlans: DefaultSelectData[] = [];

  public date = moment(new Date()).toDate();
  public time = moment(this.date).format('HH:mm:ss');
  constructor(
    public isNBF = false,
    public isUnix = false
  ) {
    super(isNBF, isUnix);
    if (!isNBF) {
      this.retentionPolicyStep = {
        RetentionUseDefaultSettings: true,
        keepNumberOfVersions: false,
        RetentionNumberOfVersions: 3,
        deleteVersionsOlderThan: false,
        deleteVersionsOlderThanCount: 1,
        deleteVersionsOlderThanPeriod: 2,
        valid: true
      };
      this.scheduleStep = {
        ScheduleType: 'noschedule',
        specificDateGroup: { date: this.date, time: this.time },
        StopAfterEnabled: false,
        stopAfterHours: 0,
        stopAfterMinutes: 0,
        ForceMissedSchedule: false,
        overdueAfterSuccessEnabled: false,
        overdueAfterSuccessAmount: 7,
        overdueAfterSuccessPeriod: 1,
        syncBeforeRun: false,
        valid: true
      };
      this.scheduleAdvancedStep = {
        forceFull: new ScheduleFormSettings(false, new RecurringSchedule(isUnix)),
        blockLevel: new ScheduleFormSettings(),
        scheduleDiff: new ScheduleFormSettings(null, null),
        scheduleTLog: new ScheduleFormSettings(null, null),
        valid: true
      };
      this.simpleScheduleStep = {
        startFromDate: moment().format('YYYY-MM-DDT00:00:00'),
        startFromTime: this.time,
        scheduleTemplate: '',
        fullScheduleEnabled: true,
        fullBackupEveryHours: 168,
        fullBackupEveryMinutes: 0,
        blockLevelBackup: true,
        blockLevelBackupEveryHours: 24,
        blockLevelBackupEveryMinutes: 0,
        scheduleDiffEnabled: false,
        differentialBackupEveryHours: 0,
        differentialBackupEveryMinutes: 0,
        scheduleTLogEnabled: false,
        tLogBackupEveryHours: 0,
        tLogBackupEveryMinutes: 0,
        valid: true
      };
    } else {
      this.compressionEncryptionStep.hint = '';
      this.retentionPolicyStep = {
        RetentionUseDefaultSettings: false,
        deleteVersionsOlderThanCount: 3,
        deleteVersionsOlderThanPeriod: 2,
        intelligentRetention: true, // like at Agent side
        GFSPolicySettings: getDefaultGFSPolicySettings(),
        valid: true
      };
      this.scheduleStep = {
        ScheduleType: isUnix ? 'noschedule' : null,
        StopAfterEnabled: false,
        stopAfterHours: 0,
        stopAfterMinutes: 0,
        ForceMissedSchedule: false,
        incrementalData: {
          enabled: !isUnix,
          recurringPeriod: 'Daily',
          repeatEveryCount: 1,
          weeklyDayOfWeek: [false, true, true, true, true, true, false],
          occurrence: WeekNumber.First,
          dayOfWeek: DayOfWeek.Wednesday,
          dayOfMonthCount: 1,
          dailyFreqPeriodOption: 'OccursAt',
          occursAtTime: '12:00',
          occursEveryCount: 1,
          occursEveryPeriod: 'hours',
          occursEveryFromTime: '09:00',
          occursEveryToTime: '21:00',
          startFromDate: moment().format('YYYY-MM-DDT00:00:00')
        },
        fullBackupData: {
          enabled: !isUnix,
          recurringPeriod: 'Monthly',
          weeklyDayOfWeek: [true, false, false, false, false, false, false],
          occurrence: WeekNumber.First,
          dayOfWeek: DayOfWeek.Wednesday,
          dayOfMonthCount: 1,
          dailyFreqPeriodOption: 'OccursAt',
          occursAtTime: '00:00'
        },
        syntheticData: { ...syntheticFullDefaults },
        syncBeforeRun: false,
        valid: true
      };
      this.archiveConsistencyStep = {
        useFullConsistencyCheck: true,
        RunRestoreVerificationOn: 0,
        minutes: 1,
        seconds: 0,
        numCpu: 2,
        memoryInMb: 4096,
        failureMinutes: defaultFailureMinutes,
        valid: true
      };

      if (!isUnix) {
        this.retentionPolicyStep.GFSPolicySettings = getDefaultGFSPolicySettings();
      }
    }

    if (!isUnix) {
      this.prePostActionsStep.enableBackupChain = false;
      this.prePostActionsStep.backupChainNextPlan = '';
      this.prePostActionsStep.executeChainPlanOnSuccess = false;
      this.prePostActionsStep.ForceFullNextPlan = false;
    }
  }
}
